import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DirtyComponent } from 'src/app/helpers/CanDeactivate/dirty-component';
import { CustomValidator } from 'src/app/helpers/custom.validator';
import { Patterns } from 'src/app/helpers/patterns';
import { Lookup } from 'src/app/models/Lookup';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LookupsService } from 'src/app/services/lookups.service';
import Swal from 'sweetalert2';
import { Profile } from '../models/Profile';

export interface DialogData {
  details: Profile
}
@Component({
  selector: 'app-profile-form',
  templateUrl: './profile-form.component.html'
})
export class ProfileFormComponent extends DirtyComponent implements OnInit {
  override formGroup!: FormGroup;
  dataLoading = false;
  loading = false;
  isSubmitted = false;

  details = this.data.details;

  nationalities: Lookup[] = [];
  genderList: Lookup[] = [];

  today = new Date();
  maxDate = new Date();

  selectedCountry: any;

  // image file
  logo: any;
  logoSrc!: string;
  maxFileSize = 0.5;
  fileExt!: string;
  fileTypeError = false;
  fileSizeError = false;

  constructor(
    override dialogRef: MatDialogRef<ProfileFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private formBuilder: FormBuilder,
    private service: AuthenticationService,
    public lookupsService: LookupsService,
    translate: TranslateService
  ) { super(translate); }

  ngOnInit(): void {
    this.maxDate = new Date(this.today.setFullYear(this.today.getFullYear() - 18));
    this.buildForm();
    this.getNationalities();
    this.getGender();
  }

  override canDeactivate(): boolean {
    return this.formGroup.dirty || this.logo ? true : false;
  };

  buildForm() {
    this.formGroup = this.formBuilder.group({
      fName: [null, [
        CustomValidator.noWhiteSpace,
        Validators.maxLength(50),
        // Validators.pattern(Patterns.noSpecialTxt),
        Validators.required
      ]],
      lName: [null, [
        CustomValidator.noWhiteSpace,
        Validators.maxLength(50),
        // Validators.pattern(Patterns.noSpecialTxt),
        Validators.required
      ]],
      phoneNumber: [null, Validators.required],
      email: [null, [
        CustomValidator.noWhiteSpace,
        Validators.maxLength(50),
        Validators.pattern(Patterns.email)
      ]],
      nationalityId: [null, Validators.required],
      birthDate: [null, Validators.required],
      gender: [],
    });

    this.formGroup.patchValue(this.details);
    if (this.details.dateOfBirth) {
      this.formGroup.controls['birthDate'].setValue(this.details.dateOfBirth.gregorian);
    }
    this.logoSrc = this.details.profilePicture;
    this.selectedCountry = this.details.countryflag;
  }

  hasError(controlName: string, errorName: string) {
    return this.formGroup.controls[controlName].hasError(errorName);
  }


  getNationalities() {
    this.lookupsService.getNationalities()
      .subscribe(
        data => {
          this.nationalities = data as [];
        }, err => {
          console.error(err);
        });
  }
  getGender() {
    this.lookupsService.GetGender()
      .subscribe(
        data => {
          this.genderList = data as [];
          this.formGroup.controls['gender'].setValue(this.details.gender.lookupcode)
        }, err => {
          console.error(err);
        });
  }

  saveProfile() {
    this.isSubmitted = true;
    if (this.formGroup.valid) {
      this.loading = true;
      let data = this.formGroup.value;
      data.phoneflag = this.formGroup.controls['phoneNumber'].value.countryCode;
      data.phoneCode = this.formGroup.controls['phoneNumber'].value.dialCode;
      data.phoneNumber = this.formGroup.controls['phoneNumber'].value.number;

      this.service.updateUserProfile(data, this.logo).subscribe(response => {
        this.loading = false;
        this.updateSuccess(response);
      }, error => {
        this.loading = false;
        Swal.fire({
          title: this.translate.instant('swalMsg.Failed'),
          text: error.detail,
          icon: 'error',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
      });
    }
  }

  updateSuccess(response: any) {
    let message = this.translate.instant('swalMsg.updatedsuccessfully');
    let emailChanged = false;
    if (response.errorMessages && response.errorMessages.length) {
      message = response.errorMessages[0];
      emailChanged = true;
    }
    Swal.fire({
      title: this.translate.instant('swalMsg.Success'),
      text: message,
      icon: 'success',
      confirmButtonText: this.translate.instant('swalMsg.Ok')
    }).then((result) => {
      this.dialogRef.close(true);
      if (emailChanged) {
        this.service.logout();
      }
    })
  }


  // File preview with validation
  changeFileListener($event: any): void {
    const file: File = $event.target.files[0];
    if (file && this.validateFile(file)) {
      this.readThis(file);
      this.logo = file;
    } else {
      this.logoSrc = '';
      this.logo = undefined;
    }
  }
  readThis(file: File): void {
    const myReader: FileReader = new FileReader();
    myReader.readAsDataURL(file);
    myReader.onloadend = (e) => {
      this.logoSrc = myReader.result as string;
    };
  }
  validateFile(file: File): any {
    if (this.fileType(file.name)) {
      this.fileTypeError = false;
      if ((file.size / (1024 * 1024)) <= this.maxFileSize) {
        this.fileSizeError = false;
      } else {
        this.fileSizeError = true;
        return false;
      }
    } else {
      this.fileTypeError = true;
      return false;
    }
    return true;
  }
  fileType(fileName: string): any {
    const extension = fileName.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'jpeg':
      case 'jpg':
        return 'jpg';
      case 'png':
        return 'png';
      default:
        return false;
    }
  }
}
